import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/styles';
import { FormProvider, useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Button,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormInput from '../ReactHookForm/FormInput/FormInput';
import SectionHeader from '../ReactHookForm/SectionHeader';
import cn from 'classnames';
import { useHistory } from 'react-router';
import { getCallConfigs, updateCallConfigs } from '../../services/callConfig.service';
import { AppContext } from '../../AppContext';

const styles = theme => ({
  gridContainer: {
    // flex: 1,
    // alignSelf: 'stretch',
  },
  form: {
    width: '100%',
  },
});

const FORM_FIELDS = {
  TALK_TO_PEOPLE: 'talkToPeople',
  MIN_TIME_OF_DAY: 'minTimeOfDay',
  MAX_TIME_OF_DAY: 'maxTimeOfDay',
  DAYS_OF_WEEK: 'daysOfWeek',
  USE_EMAIL: 'useEmail',
  AUTO_CALL: 'autoCall',
  VOICEMAIL_TEMPLATE: 'voicemailTemplate',
  REPORT_NAME: 'reportName',
  CLIENT_CODE: 'clientCode',
};

const formValidationSchema = Yup.object().shape({
  [FORM_FIELDS.TALK_TO_PEOPLE]: Yup.boolean().default(false),
  [FORM_FIELDS.MIN_TIME_OF_DAY]: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/, 'Invalid time format (HH:mm or HH:mm:ss)')
    .required('Min time of day is required'),
  [FORM_FIELDS.MAX_TIME_OF_DAY]: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/, 'Invalid time format (HH:mm or HH:mm:ss)')
    .required('Max time of day is required'),
  [FORM_FIELDS.DAYS_OF_WEEK]: Yup.string().required('Days of week is required'),
  [FORM_FIELDS.USE_EMAIL]: Yup.boolean().default(false),
  [FORM_FIELDS.AUTO_CALL]: Yup.boolean().default(false),
  [FORM_FIELDS.VOICEMAIL_TEMPLATE]: Yup.string().default(''),
  [FORM_FIELDS.REPORT_NAME]: Yup.string().required('Report name is required'),
  [FORM_FIELDS.CLIENT_CODE]: Yup.string().required('Client code is required'),
});

const newFormValidationSchema = Yup.object().shape({
  clientFormConfigs: Yup.array().of(formValidationSchema),
});

const DAYS_OF_WEEK = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' },
];

export const CallManagerSettingsForm = withStyles(styles)(({ classes, onSave, initialData = {} }) => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callConfigs, setCallConfigs] = useState([]);
  const [callConfigsLoading, setCallConfigsLoading] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const { dispatch } = useContext(AppContext);

  const formMethods = useForm({
    resolver: yupResolver(newFormValidationSchema),
    defaultValues: {
      clientFormConfigs: [],
    },
  });

  const { formState } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'clientFormConfigs',
  });

  function formatTimeToAMPM(timeStr) {
    if (!timeStr) return '';
    const [hour, minute] = timeStr.split(':');
    let h = parseInt(hour, 10);
    const m = minute;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    return `${h}:${m} ${ampm}`;
  }

  const getClientCallConfigs = async () => {
    try {
      setCallConfigsLoading(true);
      const fetchedCallConfigs = await getCallConfigs();
      setCallConfigs(fetchedCallConfigs);

      const configsForFieldArray = fetchedCallConfigs.map(config => ({
        [FORM_FIELDS.TALK_TO_PEOPLE]: config.talkToPeople || false,
        [FORM_FIELDS.MIN_TIME_OF_DAY]: formatTimeToAMPM(config.minTimeOfDay) || '12:00 AM',
        [FORM_FIELDS.MAX_TIME_OF_DAY]: formatTimeToAMPM(config.maxTimeOfDay) || '11:59 PM',
        [FORM_FIELDS.DAYS_OF_WEEK]: config.daysOfWeek || '',
        [FORM_FIELDS.USE_EMAIL]: config.useEmail || false,
        [FORM_FIELDS.AUTO_CALL]: config.autoCall || false,
        [FORM_FIELDS.VOICEMAIL_TEMPLATE]: config.voicemailTemplate || '',
        [FORM_FIELDS.REPORT_NAME]: config.reportName || '',
        originalConfigId: config.configId,
        originalClientId: config.clientId,
        clientName: config.clientName,
        [FORM_FIELDS.CLIENT_CODE]: config.clientCode || '',
      }));

      formMethods.reset({ clientFormConfigs: configsForFieldArray });
    } catch (error) {
      console.log('Error fetching or processing call configs:', error);
    } finally {
      setCallConfigsLoading(false);
    }
  };

  useEffect(() => {
    getClientCallConfigs();
  }, []);

  const handleAccordionChange = panel => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const onSubmitValid = async formData => {
    setIsSubmitting(true);
    try {
      const { clientFormConfigs: dirtyClientConfigs } = formState.dirtyFields;
      const updatedConfigsPayloads = [];

      if (dirtyClientConfigs) {
        formData.clientFormConfigs.forEach((clientConfig, index) => {
          if (dirtyClientConfigs[index]) {
            const apiPayload = {
              ConfigId: clientConfig.originalConfigId,
              ClientId: clientConfig.originalClientId,
              ReportName: clientConfig[FORM_FIELDS.REPORT_NAME],
              MinTimeOfDay: clientConfig[FORM_FIELDS.MIN_TIME_OF_DAY],
              MaxTimeOfDay: clientConfig[FORM_FIELDS.MAX_TIME_OF_DAY],
              DaysOfWeek: clientConfig[FORM_FIELDS.DAYS_OF_WEEK],
              TalkToPeople: clientConfig[FORM_FIELDS.TALK_TO_PEOPLE],
              UseEmail: clientConfig[FORM_FIELDS.USE_EMAIL],
              AutoCall: clientConfig[FORM_FIELDS.AUTO_CALL],
              VoicemailTemplate: clientConfig[FORM_FIELDS.VOICEMAIL_TEMPLATE],
            };
            updatedConfigsPayloads.push(apiPayload);
          }
        });
      }

      if (updatedConfigsPayloads.length > 0) {
        await updateCallConfigs(updatedConfigsPayloads);
        dispatch({
          type: 'SET_SNACKBAR',
          snackbar: {
            isOpen: true,
            message: 'Call manager settings saved successfully',
            variant: 'success',
          },
        });
      } else {
        console.log('No changes to save.');
        dispatch({
          type: 'SET_SNACKBAR',
          snackbar: {
            isOpen: true,
            message: 'No changes to save.',
            variant: 'info',
          },
        });
      }
    } catch (error) {
      console.error('Error saving call manager settings:', error);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Error saving call manager settings',
          variant: 'error',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitInvalid = errors => {
    console.log('Form validation errors:', errors);
  };

  return (
    <div className={cn('grid-container')}>
      <FormProvider {...formMethods} schema={newFormValidationSchema}>
        <form onSubmit={formMethods.handleSubmit(onSubmitValid, onSubmitInvalid)} className={classes.form}>
          <Grid container direction="row" className={cn(classes.gridContainer)} spacing={2}>
            <Grid item xs={12} container>
              <Button
                style={{ backgroundColor: '#315382', color: '#fff' }}
                variant="contained"
                type="button"
                onClick={() => {
                  history.goBack();
                }}
              >
                Back
              </Button>
            </Grid>

            <SectionHeader title="Call Manager Settings" />

            {callConfigsLoading ? (
              <Grid
                item
                xs={12}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
              >
                <CircularProgress />
              </Grid>
            ) : callConfigs.length > 0 ? (
              fields.map((item, index) => (
                <Grid item xs={12} key={item.originalConfigId}>
                  <Accordion
                    expanded={expandedAccordion === item.originalConfigId}
                    onChange={handleAccordionChange(item.originalConfigId)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography style={{ fontWeight: 'bold' }}>{item.clientName} - {item.clientCode}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* <Grid item xs={4}>
                          <FormInput
                            type="text"
                            name={`clientFormConfigs.${index}.${FORM_FIELDS.REPORT_NAME}`}
                            label="Report Name"
                          />
                        </Grid> */}

                        <Grid item xs={4}>
                          <FormInput
                            type="text"
                            name={`clientFormConfigs.${index}.${FORM_FIELDS.MIN_TIME_OF_DAY}`}
                            label="Min Time of Day"
                            placeholder="HH:mm or HH:mm:ss"
                            inputProps={{ readOnly: true }}
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <FormInput
                            type="text"
                            name={`clientFormConfigs.${index}.${FORM_FIELDS.MAX_TIME_OF_DAY}`}
                            label="Max Time of Day"
                            placeholder="HH:mm or HH:mm:ss"
                            inputProps={{ readOnly: true }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container>
                            <Grid item xs={4}>
                              <div>
                                <label style={{ textAlign: 'left', display: 'flex' }}>Days of Week</label>
                                <Controller
                                  name={`clientFormConfigs.${index}.${FORM_FIELDS.DAYS_OF_WEEK}`}
                                  control={formMethods.control}
                                  render={({ field }) => {
                                    const currentDaysString = field.value || '';
                                    const selectedDaysArray = currentDaysString ? currentDaysString.split(',') : [];

                                    const handleCheckboxChange = dayValue => {
                                      const newSelectedDaysArray = selectedDaysArray.includes(dayValue)
                                        ? selectedDaysArray.filter(d => d !== dayValue)
                                        : [...selectedDaysArray, dayValue].sort((a, b) => Number(a) - Number(b));
                                      field.onChange(newSelectedDaysArray.join(','));
                                    };

                                    return (
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '8px',
                                          marginTop: '8px',
                                        }}
                                      >
                                        {DAYS_OF_WEEK.map(day => (
                                          <div key={day.value} style={{ display: 'flex', alignItems: 'center' }}>
                                            <input
                                              type="checkbox"
                                              id={`clientFormConfigs.${index}.${FORM_FIELDS.DAYS_OF_WEEK}-${day.value}`}
                                              checked={selectedDaysArray.includes(day.value)}
                                              onChange={() => handleCheckboxChange(day.value)}
                                              style={{ marginRight: '8px' }}
                                              disabled
                                            />
                                            <label
                                              htmlFor={`clientFormConfigs.${index}.${FORM_FIELDS.DAYS_OF_WEEK}-${day.value}`}
                                            >
                                              {day.label}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }}
                                />
                              </div>
                            </Grid>

                            <Grid item xs={8}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px' }}>
                                {/* <div className="row-start-center">
                                  <Controller
                                    name={`clientFormConfigs.${index}.${FORM_FIELDS.TALK_TO_PEOPLE}`}
                                    control={formMethods.control}
                                    render={({ field }) => (
                                      <input
                                        type="checkbox"
                                        id={`clientFormConfigs.${index}.${FORM_FIELDS.TALK_TO_PEOPLE}`}
                                        onChange={e => field.onChange(e.target.checked)}
                                        checked={field.value}
                                        disabled
                                      />
                                    )}
                                  />
                                  <label htmlFor={`clientFormConfigs.${index}.${FORM_FIELDS.TALK_TO_PEOPLE}`}>
                                    Talk to People
                                  </label>
                                </div> */}
                                {/* <div className="row-start-center">
                                  <Controller
                                    name={`clientFormConfigs.${index}.${FORM_FIELDS.USE_EMAIL}`}
                                    control={formMethods.control}
                                    render={({ field }) => (
                                      <input
                                        type="checkbox"
                                        id={`clientFormConfigs.${index}.${FORM_FIELDS.USE_EMAIL}`}
                                        onChange={e => field.onChange(e.target.checked)}
                                        checked={field.value}
                                      />
                                    )}
                                  />
                                  <label htmlFor={`clientFormConfigs.${index}.${FORM_FIELDS.USE_EMAIL}`}>
                                    Use Email
                                  </label>
                                </div> */}
                                <div className="row-start-center">
                                  <Controller
                                    name={`clientFormConfigs.${index}.${FORM_FIELDS.AUTO_CALL}`}
                                    control={formMethods.control}
                                    render={({ field }) => (
                                      <input
                                        type="checkbox"
                                        id={`clientFormConfigs.${index}.${FORM_FIELDS.AUTO_CALL}`}
                                        onChange={e => field.onChange(e.target.checked)}
                                        checked={field.value}
                                        disabled
                                      />
                                    )}
                                  />
                                  <label htmlFor={`clientFormConfigs.${index}.${FORM_FIELDS.AUTO_CALL}`}>
                                    Auto Call
                                  </label>
                                </div>
                              </div>

                              {/* <div>
                                <FormInput
                                  type="textarea"
                                  name={`clientFormConfigs.${index}.${FORM_FIELDS.VOICEMAIL_TEMPLATE}`}
                                  label="Voicemail Template"
                                />
                              </div> */}
                            </Grid>
                              <div style={{marginTop: 16}}>
                                <Button
                                  style={{ backgroundColor: '#315382', color: '#fff' }}
                                  variant="contained"
                                  disabled={isSubmitting}
                                  onClick={() => {
                                    history.push(`/clients/${item.originalClientId}`);
                                  }}
                                >
                                  Edit Client Configuration
                                </Button>
                              </div>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography>No call configurations found.</Typography>
              </Grid>
            )}

            <Grid item xs={12} container style={{ marginTop: 20 }}>
              {/* <Button
                style={{ backgroundColor: '#315382', color: '#fff' }}
                variant="contained"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress style={{ fill: '#fff', color: '#fff', marginRight: 10 }} size={25} />
                    Saving
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button> */}
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </div>
  );
});
